const DB_NAME = 'PwappySnapshotDB';
const DB_VERSION = 1;
const STORE_NAME = 'snapshots';

class IndexedDBHelper {
  constructor() {
    this.db = null;
  }

  /**
   * データベースへの接続と初期化
   * @returns {Promise<IDBDatabase>} データベースインスタンス
   */
  async connect() {
    if (this.db) {
      return this.db;
    }
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = (event) => {
        console.error("IndexedDB error:", event.target.error);
        reject("IndexedDBの接続に失敗しました。");
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          // 'key'を複合キーとして使用するため、キーパスを指定せずに作成
          const objectStore = db.createObjectStore(STORE_NAME, { autoIncrement: true });
          // 検索用に複合インデックスを作成
          objectStore.createIndex('app_version_idx', ['identKey', 'appKey', 'version'], { unique: false });
        }
      };
    });
  }

  /**
   * スナップショットを保存
   * @param {object} snapshot - 保存するスナップショットデータ
   * @returns {Promise<number>} 保存されたレコードのID
   */
  async saveSnapshot(snapshot) {
    const db = await this.connect();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.add(snapshot);

      request.onsuccess = (event) => {
        resolve(event.target.result);
      };
      request.onerror = (event) => {
        console.error("スナップショットの保存に失敗しました:", event.target.error);
        reject(event.target.error);
      };
    });
  }

  /**
   * 指定されたキーに一致するスナップショットを取得
   * @param {string} identKey
   * @param {string} appKey
   * @param {string} version
   * @returns {Promise<Array<object>>} スナップショットの配列
   */
  async getSnapshots(identKey, appKey, version) {
    const db = await this.connect();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('app_version_idx');
      // 複合キーで検索
      const keyRange = IDBKeyRange.only([identKey, appKey, version]);
      const request = index.getAll(keyRange);

      request.onsuccess = (event) => {
        // 結果にプライマリキーを追加して返す
        const cursorRequest = index.openCursor(keyRange);
        const resultsWithKeys = [];
        let i = 0;
        cursorRequest.onsuccess = (e) => {
          const cursor = e.target.result;
          if (cursor) {
            const value = cursor.value;
            value.id = cursor.primaryKey; // プライマリキーをオブジェクトに追加
            resultsWithKeys.push(value);
            cursor.continue();
          } else {
            // 結果をtimestampの降順でソート
            resultsWithKeys.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            resolve(resultsWithKeys);
          }
        };
        cursorRequest.onerror = (e) => reject(e.target.error);
      };
      request.onerror = (event) => {
        console.error("スナップショットの取得に失敗しました:", event.target.error);
        reject(event.target.error);
      };
    });
  }

  /**
   * IDで指定された特定のスナップショットを削除
   * @param {number} id - 削除するスナップショットのプライマリキー
   * @returns {Promise<void>}
   */
  async deleteSnapshot(id) {
    const db = await this.connect();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = (event) => {
        console.error("スナップショットの削除に失敗しました:", event.target.error);
        reject(event.target.error);
      };
    });
  }

  /**
   * 指定されたキーに一致するすべてのスナップショットを削除
   * @param {string} identKey
   * @param {string} appKey
   * @param {string} version
   * @returns {Promise<void>}
   */
  async clearSnapshots(identKey, appKey, version) {
    const db = await this.connect();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('app_version_idx');
      const keyRange = IDBKeyRange.only([identKey, appKey, version]);

      const cursorRequest = index.openCursor(keyRange);
      cursorRequest.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          store.delete(cursor.primaryKey);
          cursor.continue();
        } else {
          resolve(); // カーソルが終端に達したら完了
        }
      };
      cursorRequest.onerror = (event) => {
        console.error("スナップショットのクリアに失敗しました:", event.target.error);
        reject(event.target.error);
      };
    });
  }

  /**
   * 指定された日数より古いスナップショットをすべて削除する
   * @param {number} days - この日数より古いスナップショットを削除する
   * @returns {Promise<void>}
   */
  async deleteOldSnapshots(days) {
    const db = await this.connect();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const cursorRequest = store.openCursor();

      const threshold = new Date();
      threshold.setDate(threshold.getDate() - days);

      cursorRequest.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const snapshot = cursor.value;
          const snapshotDate = new Date(snapshot.timestamp);
          if (snapshotDate < threshold) {
            // 指定日数より古いスナップショットを削除
            store.delete(cursor.primaryKey);
          }
          cursor.continue();
        } else {
          // カーソルが終端に達したら完了
          resolve();
        }
      };

      cursorRequest.onerror = (event) => {
        console.error("古いスナップショットの削除に失敗しました:", event.target.error);
        reject(event.target.error);
      };
    });
  }
}

export default new IndexedDBHelper();